import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { comics } from "@/mocks/comics";
import { formatViews } from "@/utils/comics";

const featured = comics.filter((c) => c.banner).slice(0, 5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);

  const go = useCallback((next: number) => {
    setIndex((next + featured.length) % featured.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const current = featured[index];

  return (
    <section className="relative w-full overflow-hidden rounded-lg bg-foreground-900">
      <div className="relative h-[440px] w-full md:h-[560px]">
        {featured.map((comic, i) => (
          <div
            key={comic.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={comic.banner}
              alt={`${comic.title} 焦点推荐`}
              title={`${comic.title} 免费韩漫在线阅读`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
        ))}

        <div className="relative z-10 flex h-full w-full items-end">
          <div className="w-full px-5 pb-10 md:px-12 md:pb-14">
            <div key={current.id} className="animate-float-up max-w-2xl">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  焦点推荐
                </span>
                {current.categories.map((cat) => (
                  <span
                    key={cat}
                    className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur"
                  >
                    {cat}
                  </span>
                ))}
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs text-white backdrop-blur">
                  {current.status}
                </span>
              </div>
              <h2 className="font-display text-3xl font-extrabold leading-tight text-white md:text-5xl">
                {current.title}
              </h2>
              <p className="mt-3 hidden max-w-xl text-sm leading-relaxed text-white/85 sm:block">
                {current.summary}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  立即阅读
                </Link>
                <Link
                  to={`/read/${current.id}/${current.chapterCount}`}
                  className="whitespace-nowrap rounded-md border border-white/40 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/20"
                >
                  从最新话开始
                </Link>
                <span className="flex items-center gap-4 text-xs text-white/80">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {formatViews(current.views)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-primary-400" />
                    {current.rating}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur transition-colors hover:bg-black/55 md:left-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-left-s-line text-2xl" />
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-white backdrop-blur transition-colors hover:bg-black/55 md:right-6 md:h-11 md:w-11"
        >
          <i className="ri-arrow-right-s-line text-2xl" />
        </button>

        <div className="absolute bottom-4 right-5 z-20 flex items-center gap-2 md:right-12">
          {featured.map((comic, i) => (
            <button
              key={comic.id}
              type="button"
              aria-label={`切换到 ${comic.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? "w-7 bg-primary-500" : "w-2.5 bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}